import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../services/entityCreationRegistry';

export const manuscriptReviewReportCreateCommand: SelfContainedCommand = {
  id: 'manuscript_review_report_create',
  description: 'Create a new Manuscript Review Report entity to hold beta-reader analysis results',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Title for the report (default: "Manuscript Review Report")',
        required: false,
        defaultValue: 'Manuscript Review Report'
      },
      {
        name: 'manuscript_doc_id',
        type: 'string',
        description: 'ID of the manuscript River Doc being reviewed',
        required: false,
        defaultValue: ''
      },
      {
        name: 'manuscript_name',
        type: 'string',
        description: 'Display name of the manuscript',
        required: false,
        defaultValue: ''
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'The ID of the space to create the report in',
        required: false
      }
    ]
  },

  examples: {
    good: [
      {
        input: { title: 'Review: The Iron Gate', manuscript_doc_id: 'doc_abc123' },
        expectedOutput: createSuccessResult('Manuscript Review Report created successfully', {}),
        description: 'Create a review report linked to a manuscript doc'
      }
    ],
    bad: []
  },

  performance: {
    expectedDuration: 500,
    complexity: 'low'
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const {
      title = 'Manuscript Review Report',
      manuscript_doc_id = '',
      manuscript_name = '',
      spaceId: providedSpaceId
    } = params;
    const spaceId = providedSpaceId || context.spaceId;

    try {
      const now = new Date().toISOString();

      const entityStoreModule = await import('../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const newReport = await entityStore.actions.createEntity({
        entityType: 'ManuscriptReviewReport',
        name: title.trim(),
        spaceId,
        skeleton: {
          '@type': 'ManuscriptReviewReport',
          state: 'pending',
          manuscript_doc_id,
          manuscript_name,
          reviewer_id: '',
          reviewer_name: '',
          reviewed_at: null,
          overall_score: null,
          pacing_score: null,
          engagement_score: null,
          clarity_score: null,
          prose_quality_score: null,
          dialogue_score: null,
          emotional_impact_score: null,
          overall_assessment: null,
          strengths: [],
          weaknesses: [],
          recommended_changes: [],
          total_paragraphs: 0,
          paragraphs_reviewed: 0,
          word_count: 0,
          paragraph_reviews: [],
          created_at: now
        },
        created_at: now
      } as any);

      const reportId = newReport._id || newReport.id;
      const reportLlmId = newReport.llmId;

      if (reportLlmId && reportId) {
        const { llmIdMapper } = await import('../../../utils/llmIdMapper');
        llmIdMapper.registerMapping(reportLlmId, reportId, 'document', 'ManuscriptReviewReport', newReport.name);
      }

      // Add to dataspace tree
      if (reportId && spaceId) {
        try {
          const { default: useDataspaceTreeStore } = await import('../../../stores/dataspaceTreeStore');
          useDataspaceTreeStore.getState()._applyAddNode(`${spaceId}:${reportId}`, {
            id: reportId,
            parentId: spaceId,
            pos: '!',
            expanded: false,
            kind: 'canonical'
          });
        } catch (treeError) {
          console.warn('[manuscript_review_report_create] Failed to add to tree (non-fatal):', treeError);
        }
      }

      // Open window
      let windowInfo = null;
      try {
        const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
        const result = (await useCommandRegistryStore.getState().executeCommand('space_open_window', {
          entityId: reportId
        })) as StandardToolResult;

        if (result.status === 'success') {
          windowInfo = {
            id: result.data?.windowId,
            title: title.trim(),
            appType: 'manuscript-review-report',
            entityId: reportId,
            entityLlmId: reportLlmId
          };
        }
      } catch (windowError) {
        console.error('[manuscript_review_report_create] Failed to open window:', windowError);
      }

      return createSuccessResult(
        `Manuscript Review Report created: "${title.trim()}"`,
        {
          entityId: reportId,
          report: {
            id: reportId,
            llmId: reportLlmId,
            title: title.trim(),
            entityType: 'ManuscriptReviewReport',
            state: 'pending',
            created_at: newReport.created_at
          },
          window: windowInfo
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            affectedEntities: [reportId, ...(windowInfo ? [windowInfo.id] : [])]
          } as any,
          ui: {
            refreshContext: true,
            autoFocus: windowInfo?.id,
            resultPreview: 'summary'
          }
        }
      );
    } catch (error: any) {
      return createErrorResult(
        'Report creation failed',
        `Failed to create Manuscript Review Report: ${error.message || 'Unknown error'}`,
        'execution',
        { data: { title, spaceId }, metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

entityCreationRegistry.register({
  commandId: 'manuscript_review_report_create',
  entityType: 'ManuscriptReviewReport',
  displayName: 'Manuscript Review Report',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'Newspaper',
    phosphorWeight: 'duotone'
  },
  description: 'View beta-reader analysis for a manuscript',
  canCreateInFolder: false,
  openAfterCreate: true,
  category: 'document',
  sortOrder: 2
});
